import React, { useEffect, useState } from 'react'
import { Flex, Grid } from 'lese'

import { colors } from 'Constants'

import { useInput } from 'Helpers'
import EncounterStore, {
  setFilteringSearchParams,
  clearFilteringParams,
  clearFilteringSearchParams
} from 'Store/encounters'
import UserStore from 'Store/user'
import styled from '@emotion/styled'
import { FadedText, Text } from 'Components/shared/Typography'
import { DatePickerNormal } from 'Components/shared/Calendar'
import { Button } from 'Components/shared/Button'
import ReportCSV from 'Components/shared/ReportCsv'
import JourneyService from 'Services/journey'
import Select from 'react-select'
import { ChevronDown } from 'react-feather'
import SelectStatus from 'Components/shared/SelectStatus'
import SelectOwner, { ownerOptions } from 'Components/shared/SelectOwner'
import SelectGroup, { groupOptions } from 'Components/shared/SelectGroup'
import SelectAlert from 'Components/shared/SelectAlert'
import SearchInput from './SearchInput'

const SearchInputComp = styled(Button)`
  justify-content: center;
  width: 100%;

  ${({ open }) =>
    open &&
    `
  .select-icon {
    transform: rotate(180deg);
  } 
    `}
`

const WickedSearchBG = styled(Flex)`
  display: flex;
  position: absolute;
  top: 109px;
  z-index: 10;
  width: auto;
  height: auto;
  background: ${colors.background700};
  flex-direction: column;
  justify-content: space-between;
  padding: 0.25em;
  margin: 0px;
  filter: drop-shadow(4px 2px 12px rgba(0, 0, 0, 0.4));
`
const WickedSearchOptions = styled(Flex)`
  height: 50px;
  align-items: center;
  padding: 0 0.15em 0 0.15em;
`

const WickedSearchLabel = styled(Text)`
  align-self: center;
  margin: 5px;
`

const WickedSearchFaded = styled(FadedText)`
  padding: 5px;
`

const SearchSelect = styled(Select)`
  width: 100%;
`

export default ({ isClusterUser }) => {
  const [wickedSearch, setWickedSearch] = useState(false)
  const [fromDate, setFromDate] = useState()
  const [toDate, setToDate] = useState()
  const [journeys, setJourneys] = useState(['null'])
  const [owners, setOwners] = useState(['null'])
  const [groups, setGroups] = useState(['null'])
  const [appCodes, setAppCodes] = useState(['null'])
  const [selectedJourney, setSelectedJourney] = useState()
  const [selectedAppCode, setSelectedAppCode] = useState()
  const [selectedAlertLevel, setSelectedAlertLevel] = useState()
  const [selectedStatus, setSelectedStatus] = useState()
  const [selectedOwner, setSelectedOwner] = useState()
  const [selectedGroup, setSelectedGroup] = useState()
  const patientRefHook = useInput('')
  const providerCodeHook = useInput('')
  const tCodeHook = useInput('')
  const pmhidHook = useInput('')
  const { virtualOffices } = UserStore.get()
  const { filteringParams, searchParams, sortingParams } = EncounterStore.get()

  useEffect(async () => {
    if (virtualOffices) {
      const options = await ownerOptions(virtualOffices[0].computedOfficeIds)
      setOwners(options)

      const groupOption = await groupOptions(virtualOffices[0].appCodes)
      setGroups(groupOption)

      const appCodeOptions = virtualOffices[0].appCodes.map(appCode => {
        return { value: appCode, label: appCode }
      })
      setAppCodes(appCodeOptions)

      let receivedJourneys = await JourneyService.list({ appCodes: virtualOffices[0].appCodes })
      receivedJourneys = receivedJourneys.map(journey => {
        return { value: journey.JID, label: journey.name }
      })
      setJourneys(receivedJourneys)
    }
  }, [virtualOffices])

  const doSearch = async () => {
    const keys = []
    const values = []
    let filteringParam = {}

    if (fromDate !== null && fromDate !== undefined) {
      keys.push('From Date')
      values.push(fromDate)
    }

    if (toDate !== null && toDate !== undefined) {
      keys.push('To Date')
      values.push(toDate)
    }

    if (
      selectedAlertLevel !== {} &&
      selectedAlertLevel !== null &&
      selectedAlertLevel !== undefined
    ) {
      if (selectedAlertLevel.value === -1) {
        clearFilteringParams()
      } else if (selectedAlertLevel.value !== undefined)
        filteringParam = { type: 'ALERTLEVEL', alertLevel: selectedAlertLevel.value }
    }

    if (
      selectedJourney !== null &&
      selectedJourney !== undefined &&
      selectedJourney !== {} &&
      selectedJourney.value !== undefined
    ) {
      keys.push('Journey')
      values.push(selectedJourney.value)
    }

    if (
      selectedAppCode !== null &&
      selectedAppCode !== undefined &&
      selectedAppCode !== {} &&
      selectedAppCode.value !== undefined
    ) {
      keys.push('App Code')
      values.push(selectedAppCode.value)
    }

    if (patientRefHook.value !== undefined && patientRefHook.value !== '') {
      keys.push('Patient Ref')
      values.push(patientRefHook.value)
    }

    if (providerCodeHook.value !== undefined && providerCodeHook.value !== '') {
      keys.push('Provider Code')
      values.push(providerCodeHook.value)
    }

    if (tCodeHook.value !== undefined && tCodeHook.value !== '') {
      keys.push('T-Code')
      values.push(tCodeHook.value)
    }

    if (pmhidHook.value !== undefined && pmhidHook.value !== '') {
      keys.push('PMHID')
      values.push(pmhidHook.value)
    }

    if (selectedStatus !== undefined) {
      keys.push('Status')
      values.push(selectedStatus.value)
    }

    if (selectedOwner !== undefined) {
      keys.push('Owner')
      values.push(selectedOwner.value)
    }

    if (selectedGroup !== undefined) {
      keys.push('Group')
      values.push(selectedGroup.value)
    }

    setFilteringSearchParams({ keys, values, filteringParam })
    setWickedSearch(false)
  }

  const clearSearch = async () => {
    setFromDate(undefined)
    setToDate(undefined)
    setSelectedJourney(undefined)
    setSelectedAppCode(undefined)
    setSelectedAlertLevel(undefined)
    setSelectedStatus(undefined)
    setSelectedOwner(undefined)
    setSelectedGroup(undefined)
    patientRefHook.reset()
    providerCodeHook.reset()
    tCodeHook.reset()
    pmhidHook.reset()
  }

  const resetSearch = async () => {
    await clearFilteringSearchParams()
    clearSearch()
  }

  const getCSVSearchParams = () => {
    const searchTerms = Object.keys(searchParams)
    if (searchTerms.length === 0 || searchTerms === undefined) {
      const csvToDate = new Date()
      csvToDate.setHours(0, 0, 0)

      const csvFromDate = new Date()
      csvFromDate.setMonth(csvFromDate.getMonth() - 1)
      csvFromDate.setHours(0, 0, 0)

      return {
        searchParam: {
          keys: ['From Date', 'To Date'],
          values: [csvFromDate.toISOString(), csvToDate.toISOString()]
        }
      }
    }

    return searchParams
  }

  return (
    <Flex
      style={{ background: colors.background800, gridColumn: 1 }}
      padding="0 16px"
      xAlign="space-between"
      yAlign
      separation="8px"
    >
      <ReportCSV
        sortingParams={sortingParams}
        filteringParams={filteringParams}
        searchParams={getCSVSearchParams()}
        headingParams={{ type: 'Date Range' }}
      />
      <SearchInputComp
        onClick={e => {
          e.preventDefault()
          setWickedSearch(!wickedSearch)
        }}
        open={wickedSearch}
      >
        <Text color="#ffff">Search</Text>
        <ChevronDown
          className="select-icon"
          size="16px"
          style={{ float: 'right', marginLeft: 10 }}
          color="#fff"
        />
      </SearchInputComp>
      <Button
        onClick={() => {
          resetSearch()
          setWickedSearch(false)
        }}
        color={
          Object.keys(filteringParams).length > 0 || Object.keys(searchParams).length > 0
            ? colors.blue
            : colors.backgroundInverted
        }
      >
        <Text color="#ffff">Reset</Text>
      </Button>
      {wickedSearch && (
        <WickedSearchBG>
          <Grid
            yAlign
            columns="1fr 1fr"
            rows="1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr"
            width="545px"
            rowGap="10px"
            columnGap="8px"
            style={{ margin: '0.5em' }}
          >
            <WickedSearchOptions>
              <WickedSearchLabel>Date Range</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <WickedSearchFaded>From</WickedSearchFaded>
              <DatePickerNormal
                onChange={date => {
                  setFromDate(date)
                }}
                date={fromDate}
                maxDate={toDate}
              />
              <WickedSearchFaded>To</WickedSearchFaded>
              <DatePickerNormal
                onChange={date => {
                  setToDate(date)
                }}
                date={toDate}
                minDate={fromDate}
              />
            </WickedSearchOptions>
            <WickedSearchOptions>
              <WickedSearchLabel>Alert Level</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <SelectAlert value={selectedAlertLevel} onChange={setSelectedAlertLevel} />
            </WickedSearchOptions>
            <WickedSearchOptions>
              <WickedSearchLabel>Journey</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <SearchSelect
                options={journeys}
                value={selectedJourney}
                onChange={setSelectedJourney}
              />
            </WickedSearchOptions>
            {isClusterUser && (
              <>
                <WickedSearchOptions>
                  <WickedSearchLabel>App Code</WickedSearchLabel>
                </WickedSearchOptions>
                <WickedSearchOptions>
                  <SearchSelect
                    options={appCodes}
                    value={selectedAppCode}
                    onChange={setSelectedAppCode}
                  />
                </WickedSearchOptions>
              </>
            )}
            <WickedSearchOptions>
              <WickedSearchLabel>Patient Ref</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <SearchInput hook={patientRefHook} placeholder="Patient Ref" />
            </WickedSearchOptions>
            <WickedSearchOptions>
              <WickedSearchLabel>PROVIDER/NPI</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <SearchInput hook={providerCodeHook} placeholder="PROVIDER/NPI" />
            </WickedSearchOptions>
            <WickedSearchOptions>
              <WickedSearchLabel>T-Code</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <SearchInput hook={tCodeHook} placeholder="T-Code" />
            </WickedSearchOptions>
            {/* <WickedSearchOptions>
              <WickedSearchLabel>PMHID</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <SearchInput hook={pmhidHook} placeholder="PMHID" />
            </WickedSearchOptions> */}
            <WickedSearchOptions>
              <WickedSearchLabel>Status</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <SelectStatus value={selectedStatus} onChange={setSelectedStatus} />
            </WickedSearchOptions>
            <WickedSearchOptions>
              <WickedSearchLabel>Owner</WickedSearchLabel>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <SelectOwner options={owners} value={selectedOwner} onChange={setSelectedOwner} />
            </WickedSearchOptions>
            {groups.length > 0 && (
              <>
                <WickedSearchOptions>
                  <WickedSearchLabel>Group</WickedSearchLabel>
                </WickedSearchOptions>
                <WickedSearchOptions>
                  <SelectGroup options={groups} value={selectedGroup} onChange={setSelectedGroup} />
                </WickedSearchOptions>
              </>
            )}
            <WickedSearchOptions>
              <Button
                xAlign
                color={colors.redMuted}
                onClick={() => {
                  clearSearch()
                  setWickedSearch(false)
                }}
              >
                Cancel
              </Button>
            </WickedSearchOptions>
            <WickedSearchOptions>
              <Button
                xAlign
                onClick={() => {
                  doSearch()
                }}
                style={{ margin: '0.15em', width: '75%' }}
              >
                Search
              </Button>
              <Button
                xAlign
                onClick={() => {
                  resetSearch()
                  setWickedSearch(false)
                }}
                style={{ margin: '0.15em' }}
                color={
                  Object.keys(filteringParams).length > 0 || Object.keys(searchParams).length > 0
                    ? colors.blue
                    : colors.backgroundInverted
                }
              >
                Reset
              </Button>
            </WickedSearchOptions>
          </Grid>
        </WickedSearchBG>
      )}
    </Flex>
  )
}
